import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useCookies } from 'hooks';

import { useUserContext } from './UserContext';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { COOKIE } from '../helpers/constants';
import getConfig from 'next/config';
import { trackBucketing } from '../helpers/experiments/tracking/trackBucketing';
import { getPricingIndicatorBucketedCookie } from '../components/PricingIndicator/constants';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = {
  children: ReactNode;
  showPricingIndicator?: boolean;
  pricingIndicatorVariationKey?: string | null;
};

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
  showPricingIndicator: boolean;
  pricingIndicatorVariationKey: string | null;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const {
    children,
    showPricingIndicator = false,
    pricingIndicatorVariationKey = null,
  } = params;

  const {
    publicRuntimeConfig: { PRICING_INDICATOR_EXPERIMENT_KEY },
  } = getConfig();

  const { get } = useCookies({ name: COOKIE.DD_CLIENT_ID });

  const ddClientId = get();

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof ddClientId === 'string' &&
      ddClientId.trim().length > 0
    ) {
      initSdk(ddClientId);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, ddClientId]);

  // The pricing indicator experiment is bucketed server-side to avoid a flash of content.
  // We track the bucketing event client-side directly — setting a cookie (so the server
  // knows on subsequent requests) and pushing the GTM event. trackBucketing deduplicates
  // via the cookie, so this is safe to call on every mount.
  useEffect(() => {
    if (!pricingIndicatorVariationKey) return;

    trackBucketing({
      cookieName: getPricingIndicatorBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: PRICING_INDICATOR_EXPERIMENT_KEY,
      variationKey: pricingIndicatorVariationKey,
    });
  }, [pricingIndicatorVariationKey, PRICING_INDICATOR_EXPERIMENT_KEY]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
      showPricingIndicator,
      pricingIndicatorVariationKey,
    }),
    [sdkContext, showPricingIndicator, pricingIndicatorVariationKey],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
